import ActChips from '@/components/act/ActChips'
import ActTimeSlider from '@/components/act/ActTimeSlider'
import {
  ActCatalog,
  CatalogFocus,
  CatalogSection,
  FOCUS_CORRECTNESS,
  FOCUS_STRATEGY,
  FOCUS_TIME,
} from '@/lib/actModes'
import { cn } from '@/lib/utils'

export interface CommitState {
  predictedSeconds: number
  predictedCorrect: boolean | null
  strategy: string | null
}

/** The slider keeps his last call, so a run of similar questions is one tap. */
export const EMPTY_COMMIT: CommitState = {
  predictedSeconds: 45,
  predictedCorrect: null,
  strategy: null,
}

/** Whether the focus's one commitment has actually been made. */
export function commitIsReady(focus: CatalogFocus | null, state: CommitState): boolean {
  if (!focus) return false
  switch (focus.key) {
    case FOCUS_TIME: return state.predictedSeconds > 0
    case FOCUS_CORRECTNESS: return state.predictedCorrect != null
    case FOCUS_STRATEGY: return state.strategy != null
    default: return true
  }
}

interface ActCommitStepProps {
  focus: CatalogFocus
  catalog: ActCatalog | null
  section: CatalogSection | null
  state: CommitState
  onChange: (next: CommitState) => void
}

/**
 * The one thing he commits to before the clock starts -- that commitment is the
 * whole exercise. One control, no paperwork. A new focus adds a case here and
 * an entry in the backend catalog.
 */
export default function ActCommitStep({ focus, catalog, section, state, onChange }: ActCommitStepProps) {
  const patch = (next: Partial<CommitState>) => onChange({ ...state, ...next })

  if (focus.key === FOCUS_TIME) {
    return (
      <ActTimeSlider
        value={state.predictedSeconds}
        onChange={seconds => patch({ predictedSeconds: seconds })}
        referenceSeconds={section?.secondsPerQuestion ?? null}
        referenceLabel={`${section?.label || 'test'} pace`}
      />
    )
  }

  if (focus.key === FOCUS_CORRECTNESS) {
    return (
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => patch({ predictedCorrect: true })}
          className={cn(
            'px-4 py-5 rounded-xl border-2 font-bold transition min-h-[44px]',
            state.predictedCorrect === true
              ? 'border-green-500 bg-green-50 text-green-800'
              : 'border-border hover:border-green-400 hover:bg-accent'
          )}
          aria-pressed={state.predictedCorrect === true}
        >
          I've got this
        </button>
        <button
          type="button"
          onClick={() => patch({ predictedCorrect: false })}
          className={cn(
            'px-4 py-5 rounded-xl border-2 font-bold transition min-h-[44px]',
            state.predictedCorrect === false
              ? 'border-amber-500 bg-amber-50 text-amber-800'
              : 'border-border hover:border-amber-400 hover:bg-accent'
          )}
          aria-pressed={state.predictedCorrect === false}
        >
          Probably not
        </button>
      </div>
    )
  }

  if (focus.key === FOCUS_STRATEGY) {
    const options = catalog?.categories?.[focus.planCategory || ''] || []
    return (
      <ActChips
        options={options}
        selected={state.strategy ? [state.strategy] : []}
        onToggle={key => patch({ strategy: state.strategy === key ? null : key })}
      />
    )
  }

  return null
}
